import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyDomainKey,
  ShopifyMarketKey,
  ShopifyMarketPrimaryWebPresenceToMarketKey,
  ShopifyMarketWebPresenceKey,
  ShopifyMarketWebPresenceToDomainKey,
  ShopifyMarketWebPresenceToMarketKey,
  ShopifyMarketWebPresenceToPrimaryWebPresenceForMarketKey,
  ShopifyMarketWebPresenceToShopKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyMarketWebPresenceKey,
  defaultFilterableApiIdentifiers: ["rootUrls"],
  "2022-04": {
    name: "Shopify Market Web Presence",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "id",
            shopifyFieldKey: "id",
          },
        },
      },
      {
        name: "Alternate Locales",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "alternateLocales",
            shopifyFieldKey: "alternateLocales",
          },
        },
      },
      {
        name: "Domain",
        fieldType: FieldType.BelongsTo,
        key: ShopifyMarketWebPresenceToDomainKey,
        configuration: {
          relatedModelKey: ShopifyDomainKey,
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "domain",
            graphQLSelection: "id",
            shopifyFieldKey: "domain",
          },
        },
      },
      {
        name: "Market",
        fieldType: FieldType.BelongsTo,
        key: ShopifyMarketWebPresenceToMarketKey,
        configuration: {
          relatedModelKey: ShopifyMarketKey,
        },
      },
      {
        name: "Root URLs",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "rootUrls",
            graphQLSelection: `
            locale
            url`,
            shopifyFieldKey: "rootUrls",
          },
        },
      },
      {
        name: "Subfolder Suffix",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "subfolderSuffix",
            shopifyFieldKey: "subfolderSuffix",
          },
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyMarketWebPresenceToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    // synced as part of ShopifyMarket
    syncConfig: { type: "inherit" },
  },
  "2023-10": {
    fields: [
      {
        name: "Primary Web Presence For Market",
        fieldType: FieldType.HasOne,
        key: ShopifyMarketWebPresenceToPrimaryWebPresenceForMarketKey,
        configuration: {
          relatedModelKey: ShopifyMarketKey,
          inverseFieldKey: ShopifyMarketPrimaryWebPresenceToMarketKey,
        },
      },
    ],
  },
  "2024-04": {
    fields: [
      {
        name: "Alternate Locales",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "alternateLocales",
            shopifyFieldKey: "alternateLocales",
            graphQLSelection: `
              locale
              name
              primary
              published
              marketWebPresences {
                id
              }
            `,
          },
        },
      },
    ],
  },
};

export const ShopifyMarketWebPresence = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
